import { apiClient } from './apiClient';

const FLASHCARDS_API = '/api/app/flashcards-extn';

export interface FlashcardDto {
  id: string;
  deckId: string;
  userId: string;
  question: string;
  answer: string;
  location?: string;
  locationLabel?: string;
  lastReviewedAt?: string;
  correctCount: number;
  incorrectCount: number;
  creationTime: string;
  concurrencyStamp?: string;
}

export interface FlashcardCreateDto {
  deckId: string;
  question: string;
  answer: string;
  location?: string;
  locationLabel?: string;
}

export interface FlashcardUpdateDto {
  deckId: string;
  userId: string;
  question: string;
  answer: string;
  location?: string;
  locationLabel?: string;
  concurrencyStamp: string;
}

export const flashcardsService = {
  /**
   * Get all flashcards in a deck for current user
   */
  async getMyFlashcards(deckId: string): Promise<FlashcardDto[]> {
    const response = await apiClient.get<FlashcardDto[]>(
      `${FLASHCARDS_API}/my-flashcards`,
      { params: { deckId } }
    );
    return response.data;
  },

  /**
   * Create a flashcard for current user
   */
  async createMyFlashcard(input: FlashcardCreateDto): Promise<FlashcardDto> {
    const response = await apiClient.post<FlashcardDto>(
      `${FLASHCARDS_API}/my-flashcard`,
      input
    );
    return response.data;
  },

  /**
   * Update a flashcard (current user only)
   * Fetches current flashcard first to get concurrencyStamp
   */
  async updateMyFlashcard(
    id: string,
    deckId: string,
    input: Partial<Omit<FlashcardUpdateDto, 'deckId' | 'userId' | 'concurrencyStamp'>>
  ): Promise<FlashcardDto> {
    // Fetch all flashcards to find the one we're updating
    const flashcards = await this.getMyFlashcards(deckId);
    const currentFlashcard = flashcards.find((f) => f.id === id);

    if (!currentFlashcard) {
      throw new Error(`Flashcard with id ${id} not found`);
    }

    // Merge with update data
    const updateDto: FlashcardUpdateDto = {
      question: input.question || currentFlashcard.question,
      answer: input.answer || currentFlashcard.answer,
      location: input.location !== undefined ? input.location : currentFlashcard.location,
      locationLabel: input.locationLabel !== undefined ? input.locationLabel : currentFlashcard.locationLabel,
      deckId: currentFlashcard.deckId,
      userId: currentFlashcard.userId,
      concurrencyStamp: currentFlashcard.concurrencyStamp || '',
    };

    const response = await apiClient.put<FlashcardDto>(
      `${FLASHCARDS_API}/my-flashcard/${id}`,
      updateDto
    );
    return response.data;
  },

  /**
   * Delete a flashcard (current user only)
   */
  async deleteMyFlashcard(id: string): Promise<void> {
    await apiClient.delete(`${FLASHCARDS_API}/my-flashcard/${id}`);
  },

  /**
   * Record a study session result (correct/incorrect)
   */
  async recordStudyResult(id: string, isCorrect: boolean): Promise<void> {
    await apiClient.post(
      `${FLASHCARDS_API}/record-study-result`,
      null,
      { params: { id, isCorrect } }
    );
  },
};
